// The account menu: a top-right control that opens a dropdown "profile" menu —
// the app's home for account, settings, help, and the welcome intro. It always
// mounts (so Settings / Controls / Show intro stay reachable in a local-only
// build); the account-specific items (sign in/out, delete account, sync status)
// appear only when a Supabase backend is configured.
//
// The trigger shows the signed-in email (with an avatar + a small sync-status
// dot), a "Sign in" affordance when signed out, or a plain menu icon in a
// local-only build. Clicking it drops down the menu; the sign-in email/password
// form lives inside that dropdown.
//
// Sign-in is intentionally a DESKTOP, pre-XR flow (email redirects can't happen
// inside an immersive session); the whole panel is plain DOM, hidden by the
// browser during XR like the rest of the overlay UI. Email+password only (sign
// in / create account / password reset). Errors surface through the shared modal.

import { showAlert, showConfirm, showPrompt } from "./modal";
import { syncController, type SyncStatus } from "../sync/controller";
import { isSupabaseConfigured } from "../auth/supabase-client";
import { setPasswordRecoveryHandler } from "../auth/recovery";
import {
  type AuthUser,
  deleteAccount,
  initAuth,
  onAuthState,
  requestPasswordReset,
  signInWithPassword,
  signOut,
  signUpWithPassword,
  updatePassword,
} from "../auth/session";

// Menu-item icons (feather-style, stroke = currentColor).
const ICON = {
  settings:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>',
  help: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  intro:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z"/></svg>',
  signout:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>',
  trash:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>',
  person:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
  menu: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>',
} as const;

const CSS = `
#dt-auth { position: fixed; top: 12px; right: 12px; z-index: 40;
  font: 13px/1.4 system-ui, sans-serif; color: #e8eaf0; }
#dt-auth.hidden { display: none; }

/* Trigger button */
#dt-auth .trigger { display: flex; align-items: center; gap: 8px; font: inherit;
  background: #21242e; border: 1px solid rgba(255,255,255,0.1); color: #e8eaf0;
  border-radius: 9px; padding: 5px 8px; cursor: pointer; }
#dt-auth .trigger:hover { background: #2a2e3b; }
#dt-auth .trigger .email { max-width: 180px; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; opacity: 0.9; }
#dt-auth .trigger .chev { opacity: 0.55; font-size: 10px; margin-left: -2px; }
#dt-auth .trigger svg { width: 18px; height: 18px; display: block; }

/* Avatar + sync-status dot */
#dt-auth .avatar { position: relative; width: 22px; height: 22px; border-radius: 50%;
  background: #6fa0ff; color: #11141c; font-weight: 700; font-size: 12px; flex: 0 0 auto;
  display: flex; align-items: center; justify-content: center; text-transform: uppercase; }
#dt-auth .avatar .dot { position: absolute; right: -1px; bottom: -1px; width: 8px; height: 8px;
  border-radius: 50%; border: 2px solid #21242e; background: transparent; }
#dt-auth .avatar .dot[data-s="syncing"] { background: #ffd479; }
#dt-auth .avatar .dot[data-s="synced"] { background: #8fe39a; }
#dt-auth .avatar .dot[data-s="error"] { background: #ff8080; }

/* Dropdown */
#dt-auth .menu { position: absolute; top: 100%; right: 0; margin-top: 6px; width: 252px;
  background: rgba(28,30,38,0.98); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 11px; box-shadow: 0 12px 34px rgba(0,0,0,0.5);
  padding: 6px; display: flex; flex-direction: column; gap: 2px; }
#dt-auth .mhead { padding: 8px 10px 6px; display: flex; flex-direction: column; gap: 2px; }
#dt-auth .mhead .memail { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#dt-auth .mhead .status { font-size: 11px; opacity: 0.7; min-height: 14px; }
#dt-auth .mhead .status[data-s="error"] { color: #ff8080; opacity: 1; cursor: help; }
#dt-auth .mhead .status[data-s="syncing"] { color: #ffd479; }
#dt-auth .mhead .status[data-s="synced"] { color: #8fe39a; }
#dt-auth .divider { height: 1px; background: rgba(255,255,255,0.08); margin: 4px 2px; }
#dt-auth .item { display: flex; align-items: center; gap: 10px; width: 100%; font: inherit;
  background: transparent; border: none; color: #e8eaf0; text-align: left;
  padding: 8px 10px; border-radius: 7px; cursor: pointer; }
#dt-auth .item:hover { background: rgba(255,255,255,0.08); }
#dt-auth .item svg { width: 16px; height: 16px; flex: 0 0 auto; opacity: 0.75; }
#dt-auth .item.danger { color: #ff9c9c; }
#dt-auth .item.danger:hover { background: rgba(255,128,128,0.12); }
#dt-auth .item.danger svg { opacity: 0.9; }

/* Sign-in form (inside the dropdown) */
#dt-auth .form { padding: 6px 8px 4px; display: flex; flex-direction: column; gap: 8px; }
#dt-auth .form input { background: #11141c; border: 1px solid rgba(255,255,255,0.14);
  color: #e8eaf0; border-radius: 7px; padding: 7px 9px; width: 100%; box-sizing: border-box; }
#dt-auth .row { display: flex; gap: 8px; }
#dt-auth .row button { flex: 1; }
#dt-auth button.primary { font: inherit; font-weight: 600; cursor: pointer; border-radius: 7px;
  padding: 6px 10px; background: #6fa0ff; color: #11141c; border: 1px solid transparent; }
#dt-auth button.primary:hover { background: #8bb4ff; }
#dt-auth button.ghost { font: inherit; cursor: pointer; border-radius: 7px; padding: 6px 10px;
  background: #34384a; border: 1px solid rgba(255,255,255,0.1); color: #e8eaf0; }
#dt-auth button.ghost:hover { background: #424862; }
#dt-auth button.linkline { background: none; border: none; padding: 0; cursor: pointer;
  color: #9db7e8; font-size: 12px; text-align: left; align-self: flex-start; }
#dt-auth button.linkline:hover { text-decoration: underline; }
#dt-auth .msg { font-size: 12px; line-height: 1.4; border-radius: 7px; padding: 7px 9px;
  background: rgba(143,227,154,0.12); color: #8fe39a; }
#dt-auth .msg[data-kind="info"] { background: rgba(255,212,121,0.12); color: #ffd479; }
#dt-auth .legal { font-size: 11px; line-height: 1.4; opacity: 0.7; }
#dt-auth .legal a { color: #9db7e8; text-decoration: underline; }
`;

export interface AccountMenuOptions {
  openSettings: () => void; // opens the desktop Settings overlay (ui/settings-overlay.ts)
  openHelp: () => void; // opens the desktop help/shortcuts panel (ui/desktop-help.ts)
  showWelcome: () => void; // re-opens the welcome intro card (ui/welcome.ts)
}

export function installAccountMenu(opts: AccountMenuOptions): void {
  const supa = isSupabaseConfigured();

  if (supa) {
    // On first sign-in for this device, ask before uploading pre-existing local
    // docs to the account. Declining keeps them local-only.
    syncController.setClaimDecider(async (counts) => {
      const parts = Object.entries(counts)
        .filter(([, n]) => n > 0)
        .map(([table, n]) => `${n} ${n === 1 ? table.replace(/s$/, "") : table}`);
      if (parts.length === 0) return false;
      return showConfirm(
        `You have ${parts.join(" and ")} saved on this device. Add them to your account so they sync across devices?`,
        { title: "Add to your account?", confirmLabel: "Add them" },
      );
    });
  }

  const style = document.createElement("style");
  style.textContent = CSS;
  document.head.appendChild(style);

  const root = document.createElement("div");
  root.id = "dt-auth";
  document.body.appendChild(root);

  let currentUser: AuthUser | null = null;
  let menuOpen = false;

  // In-form notice ("reset email sent", "check your email to confirm…"). Lives
  // while the dropdown stays open; cleared when it closes or auth state changes.
  let formMsg: { text: string; kind: "ok" | "info" } | null = null;
  let msgEl: HTMLElement | null = null;
  function setMsg(text: string | null, kind: "ok" | "info" = "ok"): void {
    formMsg = text ? { text, kind } : null;
    if (!msgEl) return;
    msgEl.textContent = formMsg?.text ?? "";
    msgEl.style.display = formMsg ? "" : "none";
    msgEl.dataset.kind = formMsg?.kind ?? "ok";
  }

  // Live sync status: reflected in the dropdown header text and the avatar dot.
  let status: SyncStatus = "idle";
  let statusErr: string | null = null;
  let statusEl: HTMLElement | null = null; // dropdown header (present only while open)
  let dotEl: HTMLElement | null = null; // avatar dot (present only when signed in)
  const STATUS_TEXT: Record<SyncStatus, string> = {
    idle: "Signed in",
    syncing: "Syncing…",
    synced: "Synced",
    error: "Sync error",
  };
  function applyStatus(): void {
    if (statusEl) {
      statusEl.textContent = STATUS_TEXT[status];
      statusEl.dataset.s = status;
      statusEl.title = status === "error" && statusErr ? statusErr : "";
    }
    if (dotEl) {
      dotEl.dataset.s = status;
      dotEl.title = status === "error" && statusErr ? statusErr : "";
    }
  }
  syncController.onStatus((s, err) => {
    status = s;
    statusErr = err;
    applyStatus();
  });

  // Run an auth action, surfacing any error through the modal. Redirect-based
  // methods navigate away, so a resolved promise there just means "redirecting".
  async function run(label: string, fn: () => Promise<void>): Promise<void> {
    try {
      await fn();
    } catch (e) {
      await showAlert((e as Error)?.message ?? String(e), { title: label });
    }
  }

  // Close the dropdown on any interaction outside it, or on Esc.
  function onDocPointerDown(e: PointerEvent): void {
    if (e.target instanceof Node && root.contains(e.target)) return;
    closeMenu();
  }
  function onDocKey(e: KeyboardEvent): void {
    if (e.key === "Escape" && menuOpen) {
      e.preventDefault();
      e.stopPropagation();
      closeMenu();
    }
  }
  // Detach the while-open document listeners and clear the open flag WITHOUT
  // rendering — callers that need a repaint call render() themselves.
  function teardownOpenState(): void {
    if (!menuOpen) return;
    menuOpen = false;
    formMsg = null;
    window.removeEventListener("pointerdown", onDocPointerDown, true);
    document.removeEventListener("keydown", onDocKey, true);
  }
  function openMenu(): void {
    if (menuOpen) return;
    menuOpen = true;
    window.addEventListener("pointerdown", onDocPointerDown, true);
    document.addEventListener("keydown", onDocKey, true);
    render();
  }
  function closeMenu(): void {
    if (!menuOpen) return;
    teardownOpenState();
    render();
  }
  function toggleMenu(): void {
    if (menuOpen) closeMenu();
    else openMenu();
  }

  // A single menu action row (icon + label). `danger` styles it destructive.
  function mkItem(
    icon: string,
    label: string,
    onClick: () => void,
    danger = false,
  ): HTMLButtonElement {
    const b = document.createElement("button");
    b.className = danger ? "item danger" : "item";
    b.setAttribute("role", "menuitem");
    const ico = document.createElement("span");
    ico.innerHTML = icon;
    ico.setAttribute("aria-hidden", "true");
    const lab = document.createElement("span");
    lab.textContent = label;
    b.append(ico.firstElementChild ?? ico, lab);
    b.addEventListener("click", onClick);
    return b;
  }

  const divider = (): HTMLElement => {
    const d = document.createElement("div");
    d.className = "divider";
    return d;
  };

  function deleteAccountFlow(): void {
    closeMenu();
    void run("Account deletion failed", async () => {
      const sure = await showConfirm(
        "Permanently delete your account? Your account, synced documents, and thumbnails will be removed from the server, and this browser's copy of the account's library will be cleared. This cannot be undone.",
        { title: "Delete account", confirmLabel: "Continue", danger: true },
      );
      if (!sure) return;
      const typed = await showPrompt('Type "DELETE" to confirm.', {
        title: "Delete account",
        confirmLabel: "Delete my account",
        danger: true,
        placeholder: "DELETE",
      });
      if (typed === null) return;
      if (typed.trim().toUpperCase() !== "DELETE") {
        await showAlert("Account deletion cancelled — the confirmation text didn't match.");
        return;
      }
      await deleteAccount();
      await showAlert("Your account and its data have been deleted.", {
        title: "Account deleted",
      });
    });
  }

  // The app-level items shared by every state (settings / help / intro). Each
  // closes the menu, then runs its opener.
  function appendAppItems(menu: HTMLElement): void {
    menu.append(
      mkItem(ICON.settings, "Settings", () => {
        closeMenu();
        opts.openSettings();
      }),
      mkItem(ICON.help, "Controls & shortcuts", () => {
        closeMenu();
        opts.openHelp();
      }),
      mkItem(ICON.intro, "Show welcome intro", () => {
        closeMenu();
        opts.showWelcome();
      }),
    );
  }

  // The sign-in email/password form, rendered inside the dropdown when signed
  // out. Its inputs keep their values across the message-only updates below
  // (setMsg touches just msgEl, never a full re-render).
  function buildSignInForm(): HTMLElement {
    const form = document.createElement("div");
    form.className = "form";

    const emailIn = document.createElement("input");
    emailIn.type = "email";
    emailIn.placeholder = "Email";
    emailIn.autocomplete = "email";
    const passIn = document.createElement("input");
    passIn.type = "password";
    passIn.placeholder = "Password";
    passIn.autocomplete = "current-password";

    msgEl = document.createElement("div");
    msgEl.className = "msg";
    setMsg(formMsg?.text ?? null, formMsg?.kind ?? "ok"); // restore across re-renders

    const pwRow = document.createElement("div");
    pwRow.className = "row";
    const signin = document.createElement("button");
    signin.className = "primary";
    signin.textContent = "Sign in";
    signin.onclick = () =>
      void run("Sign in failed", () => signInWithPassword(emailIn.value.trim(), passIn.value));
    const signup = document.createElement("button");
    signup.className = "ghost";
    signup.textContent = "Create account";
    signup.onclick = () =>
      void run("Sign up failed", async () => {
        const outcome = await signUpWithPassword(emailIn.value.trim(), passIn.value);
        // "session": confirmation is off — the SIGNED_IN event re-renders the
        // panel, no notice needed. "cancelled": the unsaved-work guard declined.
        if (outcome === "confirm-email") {
          // Deliberately neutral: Supabase returns this same outcome for an
          // already-registered email (no email gets sent), and naming that case
          // would leak which addresses have accounts.
          setMsg(
            "If this email is new here, check your inbox for a confirmation link. " +
              "Already registered? Sign in or use “Forgot password?”",
          );
        }
      });
    pwRow.append(signin, signup);

    // Creating an account accepts the site terms.
    const legal = document.createElement("div");
    legal.className = "legal";
    legal.append("By creating an account you agree to the ");
    const termsLink = document.createElement("a");
    termsLink.href = "/terms.html";
    termsLink.target = "_blank";
    termsLink.rel = "noopener";
    termsLink.textContent = "Terms";
    const privacyLink = document.createElement("a");
    privacyLink.href = "/privacy.html";
    privacyLink.target = "_blank";
    privacyLink.rel = "noopener";
    privacyLink.textContent = "Privacy Policy";
    legal.append(termsLink, " and ", privacyLink, ".");

    // Password reset: emails a recovery link; clicking it re-opens the app,
    // which prompts for a new password (see the recovery handler below).
    const forgot = document.createElement("button");
    forgot.className = "linkline";
    forgot.textContent = "Forgot password?";
    forgot.onclick = () =>
      void run("Password reset failed", async () => {
        const email = emailIn.value.trim();
        if (!email) {
          setMsg("Enter your email above first, then tap “Forgot password?” again.", "info");
          return;
        }
        await requestPasswordReset(email);
        setMsg(`Password reset email sent to ${email} — check your inbox.`);
      });

    form.append(emailIn, passIn, msgEl, pwRow, legal, forgot);
    return form;
  }

  function render(): void {
    root.replaceChildren();
    statusEl = null; // detached by replaceChildren; recreated below when relevant
    dotEl = null;
    msgEl = null;

    // ── Trigger ──────────────────────────────────────────────────────────
    const trigger = document.createElement("button");
    trigger.className = "trigger";
    trigger.setAttribute("aria-haspopup", "menu");
    trigger.setAttribute("aria-expanded", menuOpen ? "true" : "false");
    if (currentUser) {
      const avatar = document.createElement("span");
      avatar.className = "avatar";
      avatar.textContent = (currentUser.email || "?").charAt(0);
      dotEl = document.createElement("span");
      dotEl.className = "dot";
      avatar.appendChild(dotEl);
      const email = document.createElement("span");
      email.className = "email";
      email.textContent = currentUser.email || "Signed in";
      const chev = document.createElement("span");
      chev.className = "chev";
      chev.textContent = "▾";
      trigger.title = currentUser.email || "Account";
      trigger.append(avatar, email, chev);
    } else if (supa) {
      const ico = document.createElement("span");
      ico.innerHTML = ICON.person;
      const lab = document.createElement("span");
      lab.textContent = "Sign in";
      trigger.title = "Sign in";
      trigger.append(ico.firstElementChild ?? ico, lab);
    } else {
      const ico = document.createElement("span");
      ico.innerHTML = ICON.menu;
      trigger.title = "Menu";
      trigger.setAttribute("aria-label", "Menu");
      trigger.append(ico.firstElementChild ?? ico);
    }
    trigger.addEventListener("click", toggleMenu);
    root.append(trigger);
    applyStatus(); // reflect current status into the fresh dot (and header, if any)

    if (!menuOpen) return;

    // ── Dropdown ─────────────────────────────────────────────────────────
    const menu = document.createElement("div");
    menu.className = "menu";
    menu.setAttribute("role", "menu");

    if (currentUser) {
      const head = document.createElement("div");
      head.className = "mhead";
      const memail = document.createElement("div");
      memail.className = "memail";
      memail.textContent = currentUser.email || "Signed in";
      statusEl = document.createElement("div");
      statusEl.className = "status";
      head.append(memail, statusEl);
      menu.append(head, divider());
      applyStatus();

      appendAppItems(menu);
      menu.append(
        divider(),
        mkItem(ICON.signout, "Sign out", () => void run("Sign out failed", signOut)),
        mkItem(ICON.trash, "Delete account", deleteAccountFlow, true),
      );
    } else if (supa) {
      // Signed out: the sign-in form is the primary content, app items below.
      menu.append(buildSignInForm(), divider());
      appendAppItems(menu);
    } else {
      // Local-only build: no account, just the app menu.
      appendAppItems(menu);
    }

    root.append(menu);
  }

  // The recovery deep-link signs the user in and fires PASSWORD_RECOVERY;
  // session.ts latches it through here. Prompt (masked) until the user sets a
  // valid password or cancels; server-side rejections surface and re-prompt.
  async function promptNewPassword(): Promise<void> {
    for (;;) {
      const pw = await showPrompt("Enter a new password for your account.", {
        title: "Reset password",
        placeholder: "New password",
        password: true,
        confirmLabel: "Set password",
      });
      if (pw === null) return; // cancelled — signed in, old password unchanged
      if (pw.length < 6) {
        await showAlert("Password must be at least 6 characters.", { title: "Reset password" });
        continue;
      }
      try {
        await updatePassword(pw);
        await showAlert("Your password has been updated.", { title: "Password updated" });
        return;
      } catch (e) {
        await showAlert((e as Error)?.message ?? String(e), { title: "Password update failed" });
      }
    }
  }

  render(); // initial paint (signed-out / local-only) before auth resolves

  if (supa) {
    setPasswordRecoveryHandler(() => void promptNewPassword());
    // Re-render on every auth-state change (fires once immediately with current).
    // Any open dropdown is torn down so a sign in/out lands on a clean trigger.
    onAuthState((user) => {
      currentUser = user;
      teardownOpenState();
      render();
    });
    // Restore a persisted session / start listening. Errors here are non-fatal —
    // the panel just stays in its signed-out state.
    void initAuth().catch(() => {});
  }
}
